import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { usePlatformOutletContext } from '@/ux/Platform/usePlatFormOutletContext'
import { usePageFilters } from '../PageFilterContext/PageFilterContextProvider2'
import * as g from '@/hooks/usePage/usePage.generated'
import { PageFragment } from '@/ux/Page/Page.generated'
import { LoadingComponent } from '@/ui/Loading/LoadingComponent'

export const usePage = () => {
  const context = useContext(PageContext)
  if (!context) {
    throw new Error('usePage must be used within a PageProvider')
  }
  return context
}

const PageContext = createContext<{
  page: PageFragment
} | null>(null)

export function PageProvider({ children }: { children: ReactNode }) {
  const { appId, pageId, appKey, projectId } = usePlatformOutletContext()
  const { pageFilterInputs } = usePageFilters()
  const [getPage, { data: pageData }] = g.usePageLazyQuery()
  const [pageLoaded, setPageLoaded] = useState(false)

  const extraInputs = useMemo(
    () => [
      { name: 'appId', value: `${appId}` },
      { name: 'projectId', value: `${projectId}` },
    ],
    [appId, projectId]
  )

  useEffect(() => {
    const fetchPage = async () => {
      setPageLoaded(false)
      try {
        await getPage({
          variables: {
            appId: appKey,
            projectId,
            pageId,
            filters: pageFilterInputs ?? [],
            extraInputs,
          },
        })
        setPageLoaded(true)
      } catch (err) {
        console.error('Error fetching filters:', err)
      }
    }
    if (!pageFilterInputs) {
      return
    }
    fetchPage()
  }, [appKey, extraInputs, getPage, pageFilterInputs, pageId, projectId])

  const page = pageData?.app?.pageById as PageFragment

  const contextValue = useMemo(
    () => ({
      page,
    }),
    [page]
  )

  if (!pageLoaded) {
    return <LoadingComponent />
  }

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
}
